"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const NAVY = "#00334d";
const ACCENT = "#5cc8f5";
const EASE_OUT = [0.16, 1, 0.3, 1];
const EASE_SHUTTER = [0.76, 0, 0.24, 1];

// Timeline (ms): logo rises -> big text bursts out -> text leaves -> shutters open
const TEXT_AT = 1000;
const OUT_AT = 2500;
const OPEN_AT = 2900;
const SHUTTER_DELAY = 0.15; // s
const SHUTTER_DURATION = 0.8; // s

const MID = 5; // center letter index of the 11 letters, for the center-out stagger

function Line({ text, start, on, className = "" }) {
  return (
    <span className={`block sm:inline-block overflow-hidden py-[0.06em] ${className}`}>
      {text.split("").map((ch, k) => {
        const i = start + k;
        return (
          <motion.span
            key={k}
            className="inline-block"
            initial={{ y: "115%", opacity: 0 }}
            animate={{ y: on ? "0%" : "115%", opacity: on ? 1 : 0 }}
            transition={{ duration: 0.9, delay: on ? Math.abs(i - MID) * 0.05 : 0, ease: EASE_OUT }}
          >
            {ch === " " ? "\u00A0" : ch}
          </motion.span>
        );
      })}
    </span>
  );
}

export default function ReloaderPage({ onComplete }) {
  const [phase, setPhase] = useState("logo"); // 'logo' -> 'text' -> 'out' -> 'open'
  const doneRef = useRef(onComplete);
  doneRef.current = onComplete;

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      doneRef.current?.();
      return;
    }
    const timers = [
      setTimeout(() => setPhase("text"), TEXT_AT),
      setTimeout(() => setPhase("out"), OUT_AT),
      setTimeout(() => setPhase("open"), OPEN_AT),
      setTimeout(() => doneRef.current?.(), OPEN_AT + (SHUTTER_DELAY + SHUTTER_DURATION) * 1000 + 50),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const on = phase !== "logo";
  const out = phase === "out" || phase === "open";
  const open = phase === "open";
  const shutter = { duration: SHUTTER_DURATION, delay: SHUTTER_DELAY, ease: EASE_SHUTTER };

  return (
    <div className="fixed inset-0 z-[99999] pointer-events-none overflow-hidden" aria-hidden="true">
      {/* Shutter halves */}
      <motion.div
        className="absolute top-0 inset-x-0 h-1/2"
        style={{ backgroundColor: NAVY }}
        animate={{ y: open ? "-100%" : "0%" }}
        transition={shutter}
      />
      <motion.div
        className="absolute bottom-0 inset-x-0 h-1/2"
        style={{ backgroundColor: NAVY }}
        animate={{ y: open ? "100%" : "0%" }}
        transition={shutter}
      />

      {/* Everything that leaves before the shutters open */}
      <motion.div
        className="absolute inset-0"
        animate={{ opacity: out ? 0 : 1, y: out ? -30 : 0 }}
        transition={{ duration: 0.4, ease: EASE_SHUTTER }}
      >
        {/* Giant outlined watermark that swells behind the title */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center select-none overflow-hidden"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: on ? 1 : 0, scale: on ? 1.06 : 0.8 }}
          transition={{ duration: 1.6, ease: EASE_OUT }}
        >
          <span
            className="font-black uppercase leading-none tracking-tighter text-transparent whitespace-nowrap text-[52vw] sm:text-[34vw]"
            style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.10)" }}
          >
            IEEE
          </span>
        </motion.div>

        {/* Logo: rises from below, then lifts above the title */}
        <motion.div
          className="absolute inset-x-0 top-1/2 -mt-10 sm:-mt-12 flex justify-center"
          initial={{ y: 90, opacity: 0, scale: 0.8 }}
          animate={{ y: on ? "-19vh" : 0, opacity: 1, scale: on ? 0.85 : 1 }}
          transition={{ duration: 1.1, ease: EASE_OUT }}
        >
          <img
            src="/icon.png"
            alt=""
            className="w-20 sm:w-24 md:w-28 h-auto object-contain"
            style={{ filter: "brightness(0) invert(1)" }}
          />
        </motion.div>

        {/* Title block */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <motion.h1
            className="font-black text-white uppercase leading-[0.88] text-[17vw] sm:text-[12vw]"
            initial={{ letterSpacing: "0.2em" }}
            animate={{ letterSpacing: on ? "-0.045em" : "0.2em" }}
            transition={{ duration: 1.3, ease: EASE_OUT }}
          >
            <Line text="IEEE" start={0} on={on} />
            <span className="hidden sm:inline-block w-[0.25em]" />
            <Line text="ISIMS SB" start={4} on={on} />
          </motion.h1>

          <motion.div
            className="h-[3px] mt-5 sm:mt-6"
            style={{ backgroundColor: ACCENT }}
            initial={{ width: 0 }}
            animate={{ width: on ? 140 : 0 }}
            transition={{ delay: on ? 0.5 : 0, duration: 0.7, ease: "easeOut" }}
          />
          <motion.p
            className="mt-4 text-sm sm:text-base font-medium text-white/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: on ? 1 : 0 }}
            transition={{ delay: on ? 0.9 : 0, duration: 0.6 }}
          >
            Student Branch
          </motion.p>
        </div>
      </motion.div>

      {/* Seam drawn right before the shutters open */}
      <motion.div
        className="absolute top-1/2 inset-x-0 h-px origin-center"
        style={{ backgroundColor: ACCENT }}
        initial={{ scaleX: 0, opacity: 1 }}
        animate={open ? { scaleX: 1, opacity: [1, 1, 0] } : { scaleX: 0, opacity: 1 }}
        transition={{
          scaleX: { duration: 0.3, ease: "easeOut" },
          opacity: { duration: 0.85, times: [0, 0.6, 1] },
        }}
      />
    </div>
  );
}